import styles from './StakeholdersInfoDialog.module.css';

import React, { useEffect, useMemo, useState } from 'react';
import { Field, Formik } from 'formik';

import Api from 'components/Api';
import { bngYup } from 'components/bng/form/yup/BngYup';
import { BngField } from 'components/bng/form/BngField';
import Dialog from 'components/ui/Dialog';
import { BngCancelButton, BngPrimaryButton } from 'components/ui/Button';
import UiMsg from 'components/ui/UiMsg';
import useTranslation from 'components/hooks/useTranslation';
import BngTable from 'components/bng/ui/BngTable';
import BngDropdown from 'components/bng/ui/BngDropdown';
import BngForm from 'components/bng/form/BngForm';
import useReduxDispatch from 'components/hooks/useReduxDispatch';
import { MODALS } from 'components/ui/redux/Actions';

const StakeholderSchema = bngYup((yup) =>
  yup.object({
    fullName: yup.string().required().default(''),
    email: yup.string().email().required().default(''),
    type: yup.string().required().default(''),
    cpf: yup.string().default(''),
  })
);

function StakeholderFormDialog({ accountId, contractId, stakeholder, onChange, closeModal }) {
  const { t } = useTranslation();

  const initialValues = useMemo(() => {
    return {
      ...StakeholderSchema.default(),
      ...(stakeholder ?? {}),
    };
  }, []);

  const isNewStakeholder = _.isNil(stakeholder);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={StakeholderSchema}
      onSubmit={async (values) => {
        try {
          await Api.Account.addStakeholderToContract({ accountId, contractId, stakeholder: values });
          await onChange();
          closeModal();
        } catch (e) {
          console.error('Error while adding stakeholder', e);
          UiMsg.ajaxError(null, e);
        }
      }}
    >
      {() => (
        <Dialog
          title={t(isNewStakeholder ? 'add.billing.contact.interested' : 'edit.billing.contact.interested')}
          onClose={closeModal}
          className={`StakeholderFormDialog ${styles.StakeholdersInfoDialog}`}
          newDialogLayout={true}
        >
          <BngForm>
            <Dialog.Body>
              <Field
                component={BngField}
                name="fullName"
                label={t('name')}
                rootClassName={styles.formField}
                placeholder={t('name.placeholder')}
              />

              <Field
                component={BngField}
                name="email"
                label={t('email')}
                rootClassName={styles.formField}
                placeholder={t('email.placeholder')}
              />

              <Field
                name="type"
                label={t('stakeholderRole')}
                component={BngField}
                rootClassName={styles.formField}
                placeholder={t('type.placeholder')}
              />
            </Dialog.Body>

            <Dialog.Footer className={styles.footer}>
              <BngCancelButton onClick={closeModal}>{t('close')}</BngCancelButton>
              <BngPrimaryButton type="submit">{t('save')}</BngPrimaryButton>
            </Dialog.Footer>
          </BngForm>
        </Dialog>
      )}
    </Formik>
  );
}

export default function StakeholdersInfoDialog({ closeModal = _.noop, contractId, onChange = _.noop, accountId }) {
  const { t } = useTranslation();
  const dispatch = useReduxDispatch();
  const [stakeholders, setStakeholders] = useState([]);
  const [loading, setLoading] = useState(false);

  const findStakeholders = async () => {
    setLoading(true);
    try {
      const contractInfo = await Api.Account.fetchContractInfo(accountId);
      const legalStakeholder = {
        fullName: contractInfo.buyerInfo.fullName,
        email: contractInfo.buyerInfo.emails,
        type: t('legal.representative'),
      };
      const allStakeholders = [legalStakeholder, ...contractInfo.stakeholders];
      setStakeholders(allStakeholders);
    } catch (e) {
      console.error('Error on findStakeholders()', e);
      UiMsg.ajaxError(null, e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    findStakeholders();
  }, []);

  const openEditForm = (stakeholder) =>
    dispatch(
      MODALS.open(StakeholderFormDialog, {
        accountId,
        contractId,
        stakeholder,
        onChange: async () => {
          await findStakeholders();
          onChange();
        },
      })
    );

  const tableColumns = useMemo(
    () => [
      {
        label: t('name'),
        render: (row) => (
          <div className={styles.stakeholdersData}>
            <span>{row.fullName}</span>
          </div>
        ),
      },
      {
        label: t('email'),
        render: (row) => (
          <div className={styles.stakeholdersData}>
            <span>{row.email}</span>
          </div>
        ),
      },
      {
        label: t('stakeholderRole'),
        render: (row) => (
          <div className={styles.stakeholdersData}>
            <span>{row.type}</span>
          </div>
        ),
      },
      {
        label: t('actions'),
        render: (row) => {
          const isLegalRepresentative = row.type === t('legal.representative');
          return (
            <div className={styles.actionRow}>
              <BngDropdown
                disabled={isLegalRepresentative}
                icon="more_vert"
                overDialog={true}
                options={[
                  {
                    label: t('edit'),
                    icon: 'edit',
                    disabled: isLegalRepresentative,
                    onClick: () => {
                      if (isLegalRepresentative) {
                        return;
                      }

                      openEditForm(row);
                    },
                  },
                  {
                    label: t('delete'),
                    icon: 'delete_forever',
                    disabled: isLegalRepresentative,
                    onClick: async () => {
                      if (isLegalRepresentative) {
                        return;
                      }

                      try {
                        await Api.Account.removeStakeholderFromContract({
                          accountId,
                          contractId,
                          stakeholderId: row.id,
                        });
                        await findStakeholders();
                        onChange();
                      } catch (e) {
                        console.error('Error while removing stakeholder', e);
                        UiMsg.ajaxError(null, e);
                      }
                    },
                  },
                ]}
              />
            </div>
          );
        },
      },
    ],
    []
  );

  return (
    <Dialog
      className={`StakeholdersInfoDialog ${styles.StakeholdersInfoDialog}`}
      title={t('billing.contact.interested')}
      onClose={closeModal}
      newDialogLayout={true}
      loading={loading}
    >
      <Dialog.Body>
        <BngTable cols={tableColumns} rows={stakeholders} />
      </Dialog.Body>

      <Dialog.Footer className={styles.footer}>
        <BngPrimaryButton onClick={() => openEditForm()}>{t('add')}</BngPrimaryButton>
      </Dialog.Footer>
    </Dialog>
  );
}
